import { Directive, HostListener, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';

/**
 * Intercepts same-origin `<a href>` clicks inside its host element tree and
 * routes them through the Angular Router so a `path#fragment` href navigates
 * to the path AND sets the fragment — instead of only mutating the fragment
 * of the current page (the quick-63 bug).
 *
 * Applied ONCE at the app-root so delegated event bubbling covers every link
 * in the tree including `[innerHTML]`-rendered anchors from renderRichText /
 * raw-html-block (which cannot use `routerLink`).
 *
 * Escape hatches (fall through to browser's native navigation):
 *   - External links: http(s)://, mailto:, tel:, protocol-relative //host
 *   - Cross-origin: href.origin !== window.location.origin
 *   - download attribute present
 *   - target=_blank / _parent / _top / named frame
 *   - Modifier-key or non-primary-button click (cmd/ctrl/shift/meta/alt/middle)
 *   - event.defaultPrevented (other handlers have already claimed the click)
 *   - href is empty / null / '#' alone (anchor-only no-op)
 *
 * SSR-safe: the @HostListener only fires in a browser (click event doesn't
 * exist on the server) but we also guard with isPlatformBrowser for belt-
 * and-braces and to make unit testing deterministic.
 */
@Directive({
  selector: '[dsSameOriginLink]',
  standalone: true,
})
export class SameOriginLinkDirective {
  private readonly router = inject(Router);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (!this.isBrowser) return;
    if (event.defaultPrevented) return;

    // Primary-button clicks only
    if (event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    // Walk up from event.target to find the nearest <a>
    const anchor = (event.target as HTMLElement | null)?.closest?.('a');
    if (!anchor) return;

    // Download / new-tab escape hatches
    if (anchor.hasAttribute('download')) return;
    const target = anchor.getAttribute('target');
    if (target && target !== '_self') return;

    const rawHref = anchor.getAttribute('href');
    if (!rawHref) return;

    // Skip mailto / tel / javascript / data / about / blob — anything with a scheme
    // other than http(s) on the same origin, and protocol-relative `//host/…`.
    if (/^(mailto:|tel:|javascript:|data:|about:|blob:)/i.test(rawHref)) return;
    if (rawHref.startsWith('//')) return;

    // Resolve against the current document to get origin + pathname + hash + search
    let url: URL;
    try {
      url = new URL(rawHref, window.location.href);
    } catch {
      return;
    }

    // Cross-origin — let the browser handle it
    if (url.origin !== window.location.origin) return;

    event.preventDefault();

    const path = url.pathname || '/';
    const fragment = url.hash ? url.hash.slice(1) : undefined;
    const queryParams: Record<string, string> = {};
    url.searchParams.forEach((v, k) => {
      queryParams[k] = v;
    });

    // navigate with explicit extras — router will emit NavigationEnd with the
    // right URL AND the right fragment, anchorScrolling: 'enabled' takes care
    // of the scroll once the target component renders.
    void this.router.navigate([path], {
      fragment,
      queryParams: Object.keys(queryParams).length ? queryParams : undefined,
    });
  }
}
